<template>
  <div :class="$style.gamesCatalogueContentDefault">
    <div
      v-for="collection of collections"
      :key="collection.id"
      v-observe-visibility="{
        callback: handleVisibleCallback(collection.id),
        intersection: { rootMargin: '50px' },
        once: true,
      }"
      :class="$style.gamesCatalogueCollectionWrapper"
    >
      <CatalogueSliderCollections
        v-if="collection.id === 'collections'"
        :show-all-title="showAllTitle"
      />
      <CatalogueSliderProviders
        v-else-if="collection.id === 'providers'"
        :is-no-show="!shownCollections.includes(collection.id)"
        :show-all-title="showAllTitle"
      />
      <CatalogueSliderExploreMore
        v-else-if="collection.id === 'explore-more'"
        :show-all-title="showAllTitle"
      />
      <CatalogueRow
        v-else
        :games-list="collectionsGames[collection.id]"
        :collection="collection"
        :show-all-title="showAllTitle"
        :gtm-action="gtmAction"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onBeforeMount, reactive, ref, watch } from 'vue';
import CatalogueRow from '~/components/mainPage/catalogueRow.vue';
import CatalogueSliderCollections from '~/components/mainPage/catalogueSliderCollections.vue';
import CatalogueSliderExploreMore from '~/components/mainPage/catalogueSliderExploreMore.vue';
import CatalogueSliderProviders from '~/components/mainPage/catalogueSliderProviders.vue';
import { useLoadSliderGames } from '~/composables/useLoadSliderGames';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import type { ISsGameMainPage, ISsGame } from '~/types/softswiss/ssGames';

const HARD_CODED_COLLECTIONS = ['collections', 'providers', 'explore-more'];

const props = defineProps({
  collections: {
    type: Array as PropType<ISsGameMainPage[]> | null,
    required: true,
  },
  showAllTitle: {
    type: String,
    default: '',
  },
  gtmAction: {
    type: String,
    required: true,
  },
});

const storeSsPlayer = useStoreSsPlayer();
const storeRoot = useStoreRoot();

const isNeedToLoadGames = ref(false);
const shownCollections = ref<string[]>(props.collections.map((c) => c.id).slice(0, 3));

const collectionsGames = reactive<Record<string, ISsGame[] | null>>(
  props.collections
    ? props.collections.reduce((acc, el) => {
        acc[el.id] = null;
        return acc;
      }, {} as Record<string, ISsGame[] | null>)
    : {}
);

const { loadCollectionGames } = useLoadSliderGames();

const handleVisibleCallback = (collectionId: string): ((isVisible: boolean) => void) => {
  return (isVisible: boolean) => {
    if (isVisible && !shownCollections.value.includes(collectionId)) {
      shownCollections.value.push(collectionId);
    }
  };
};

const loadAllCollectionsGames = (force = false) => {
  shownCollections.value.forEach(async (collection) => {
    if (force && !HARD_CODED_COLLECTIONS.includes(collection)) {
      collectionsGames[collection] = await loadCollectionGames(collection);
    } else if (!collectionsGames[collection] && !HARD_CODED_COLLECTIONS.includes(collection)) {
      collectionsGames[collection] = await loadCollectionGames(collection);
    }
  });
};

watch(
  () => storeRoot.userCurrency,
  (newValue, oldValue) => {
    if (oldValue !== newValue) {
      loadAllCollectionsGames(true);
    }
  }
);

watch(
  () => shownCollections.value.length,
  () => {
    if (isNeedToLoadGames.value) {
      loadAllCollectionsGames();
    }
  }
);

watch(
  () => storeSsPlayer.isPlayerAuthorised,
  () => {
    if (isNeedToLoadGames.value) {
      loadAllCollectionsGames(true);
    }
  }
);

onBeforeMount(() => {
  isNeedToLoadGames.value = true;

  shownCollections.value = props.collections.map((c) => c.id).slice(0, 3);

  loadAllCollectionsGames();
});
</script>

<style lang="scss" module src="./catalogue.scss"></style>
